import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { api, getStatusInfo, getLevelInfo, formatDate, formatDateTime, DOCUMENT_CATEGORIES, SCORE_DESCRIPTIONS, QUALIFICATION_LEVELS } from "../lib/utils";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DashboardLayout from "../components/DashboardLayout";
import { IPCBReport } from "../components/IPCBReport";
import { InstitutionReport } from "../components/InstitutionReport";
import BadgesPanel from "../components/BadgesPanel";
import CandidateFrameworkLevelEditor from "../components/CandidateFrameworkLevelEditor";
import { ConfidencePanel, GapReportPanel, AnalysisComparisonPanel } from "../components/AIInsightsPanels";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  Upload,
  FileText,
  Brain,
  CheckCircle,
  XCircle,
  Download,
  Eye,
  Trash2,
  AlertTriangle,
  Shield,
  Award,
  Clock,
  User,
  Briefcase,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Star,
  FileCheck,
  Loader2,
  Camera,
  X,
  Printer,
  MessageSquare,
  Video,
  Send,
  UserCheck,
  RotateCcw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CandidateDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const [candidate, setCandidate] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [awards, setAwards] = useState([]);
  const [ipcbData, setIpcbData] = useState(null);
  const [institutionData, setInstitutionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("");
  const [customCategoryText, setCustomCategoryText] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [scores, setScores] = useState({});
  const [scoreNotes, setScoreNotes] = useState({});
  const [savingScores, setSavingScores] = useState(false);
  const [iqaDialogOpen, setIqaDialogOpen] = useState(false);
  const [iqaDecision, setIqaDecision] = useState({ decision: "", notes: "" });
  const [generatingReports, setGeneratingReports] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState("");  // Empty by default - show blank until selection
  const [selectedAwardTitle, setSelectedAwardTitle] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [issuingReportIds, setIssuingReportIds] = useState(false);
  const [printHintOpen, setPrintHintOpen] = useState(false);
  const [adminOverrideDialogOpen, setAdminOverrideDialogOpen] = useState(false);
  const [adminOverridePassword, setAdminOverridePassword] = useState("");
  
  // Interview Questions States
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [interviewSchedule, setInterviewSchedule] = useState({
    slot_1: "",
    slot_2: "",
    slot_3: "",
    meeting_link: "",
    meeting_platform: "google_meet",
    expert_name: "",
    expert_email: "",
    expert_organization: ""
  });
  const [questionResults, setQuestionResults] = useState({});
  const [expertFeedback, setExpertFeedback] = useState("");
  const [iqaInterviewRemarks, setIqaInterviewRemarks] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  // Written interview states
  const [writtenInterviewDialogOpen, setWrittenInterviewDialogOpen] = useState(false);
  const [writtenInterviewData, setWrittenInterviewData] = useState({
    deadline: "",
    assessor_email: "",
    assessor_name: "",
    assessor_organization: "",
  });
  const [sendingWrittenInterview, setSendingWrittenInterview] = useState(false);
  const [resendingInterview, setResendingInterview] = useState(false);
  const [resendDialog, setResendDialog] = useState({ open: false, target: "candidate", deadline: "" });
  const [adminOverrideJustification, setAdminOverrideJustification] = useState("");
  const [submittingOverride, setSubmittingOverride] = useState(false);
  // Individual competency override remarks and scores
  const [competencyOverrideRemarks, setCompetencyOverrideRemarks] = useState({});
  const [competencyOverrideScores, setCompetencyOverrideScores] = useState({});
  const [frameworks, setFrameworks] = useState([]);
  // Request More Info states
  const [requestInfoDialogOpen, setRequestInfoDialogOpen] = useState(false);
  const [requestInfoMessage, setRequestInfoMessage] = useState("");
  const [requestInfoItems, setRequestInfoItems] = useState("");
  const [sendingInfoRequest, setSendingInfoRequest] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState("");
  const ipcbReportRef = useRef(null);
  const institutionReportRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [candidateRes, competenciesRes, awardsRes, frameworksRes] = await Promise.all([
        api.get(`/candidates/${id}`),
        api.get("/competencies"),
        api.get("/awards"),
        api.get("/frameworks"),
      ]);
      setCandidate(candidateRes.data);
      setCompetencies(competenciesRes.data);
      setAwards(awardsRes.data);
      setFrameworks(frameworksRes.data.filter(f => f.status === "active"));
      
      // Fetch IPCB and Institution data
      if (candidateRes.data.ipcb_id) {
        try {
          const ipcbRes = await api.get(`/ipcbs/${candidateRes.data.ipcb_id}`);
          setIpcbData(ipcbRes.data);
        } catch (e) {
          console.log("IPCB not found");
        }
      }
      if (candidateRes.data.institution_id) {
        try {
          const instRes = await api.get(`/institutions/${candidateRes.data.institution_id}`);
          setInstitutionData(instRes.data);
        } catch (e) {
          console.log("Institution not found");
        }
      }
      
      // Set selected level from AI recommendation
      if (candidateRes.data.ai_recommendation?.recommended_level) {
        setSelectedLevel(candidateRes.data.ai_recommendation.recommended_level);
      }
      
      // Set selected award title from AI recommendation
      if (candidateRes.data.ai_recommendation?.primary_award) {
        setSelectedAwardTitle(candidateRes.data.ai_recommendation.primary_award);
      }
      
      // If candidate already has a final award selected, use that
      if (candidateRes.data.final_award) {
        setSelectedAwardTitle(candidateRes.data.final_award);
      }
      
      // Initialize scores - merge existing with AI auto-fill for missing
      const existingScores = {};
      const existingNotes = {};
      
      // First, load any existing scores from database
      if (candidateRes.data.scores && candidateRes.data.scores.length > 0) {
        candidateRes.data.scores.forEach(s => {
          existingScores[s.competency_id] = s.assessor_score;
          existingNotes[s.competency_id] = s.assessor_notes || "";
        });
      }
      
      // Then, auto-fill any MISSING competencies from AI analysis
      if (candidateRes.data.ai_recommendation?.competency_matches) {
        candidateRes.data.ai_recommendation.competency_matches.forEach(match => {
          // Only auto-fill if no score exists for this competency
          if (existingScores[match.competency_id] === undefined) {
            const strength = match.evidence_strength?.toLowerCase() || '';
            let score = 0;
            
            // Scoring logic:
            // strong = 5, adequate/good = 4, partial = 3, weak/no_evidence = 0
            if (strength === 'strong') score = 5;
            else if (strength === 'adequate' || strength === 'good') score = 4;
            else if (strength === 'partial') score = 3;
            // weak, no_evidence = score 0 (requires override)
            else score = 0;
            
            existingScores[match.competency_id] = score;
            
            // Show clear message for missing evidence (only weak and no_evidence)
            if (strength === 'no_evidence' || strength === 'weak') {
              existingNotes[match.competency_id] = `⚠️ INSUFFICIENT EVIDENCE: ${match.evidence_provided || match.evidence_summary || 'No evidence found - requires override'}`;
            } else {
              existingNotes[match.competency_id] = match.evidence_provided || match.evidence_summary || "";
            }
          }
        });
      }
      
      setScores(existingScores);
      setScoreNotes(existingNotes);
    } catch (error) {
      toast.error("Failed to load candidate");
      navigate("/candidates");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/upload-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Update candidate with photo
      await api.put(`/candidates/${id}`, { photo_url: response.data.logo_url });
      toast.success("Photo uploaded");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadCategory) {
      toast.error("Please select a file and category");
      return;
    }

    // For "Other Supporting Evidence", require custom category text
    if (uploadCategory === "Other Supporting Evidence" && !customCategoryText.trim()) {
      toast.error("Please specify the document type for 'Other Supporting Evidence'");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      // Build URL with category and optional custom_category
      let url = `/candidates/${id}/documents?category=${encodeURIComponent(uploadCategory)}`;
      if (uploadCategory === "Other Supporting Evidence" && customCategoryText.trim()) {
        url += `&custom_category=${encodeURIComponent(customCategoryText.trim())}`;
      }
      
      await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setUploadDialogOpen(false);
      setUploadCategory("");
      setCustomCategoryText("");
      fetchData();
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await api.delete(`/documents/${documentId}`);
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleInviteCandidate = async () => {
    setSendingInvite(true);
    try {
      await api.post(`/candidates/${id}/invite`);
      toast.success(`Invitation email sent to ${candidate.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRunAnalysis = async () => {
    if (!selectedFramework) {
      toast.error("Please choose a Framework before running analysis.");
      return;
    }
    if (!selectedLevel) {
      toast.error("Please choose a Qualification Level before running analysis.");
      return;
    }
    setAnalyzing(true);
    try {
      // Persist framework + level on the candidate so the badge engine,
      // reports, and verify page all stay in sync. Done BEFORE the AI
      // call so the analysis ID-of-record is locked in even if the AI
      // request itself fails or is retried.
      try {
        await api.put(`/candidates/${id}/framework-level`, {
          framework_id: selectedFramework,
          qualification_level_code: selectedLevel,
        });
      } catch (persistErr) {
        // Non-fatal — analysis can still run; just warn.
        console.warn("Could not persist framework/level:", persistErr);
      }

      const payload = { target_level: selectedLevel };
      if (selectedFramework) payload.framework_id = selectedFramework;
      const response = await api.post(`/candidates/${id}/analyze`, payload);
      toast.success("Credentia analysis complete");
      
      // Auto-populate scores based on AI competency matches
      const aiRecommendation = response.data?.ai_recommendation;
      if (aiRecommendation?.competency_matches) {
        const autoScores = {};
        const autoNotes = {};
        
        aiRecommendation.competency_matches.forEach(match => {
          // Map evidence_strength to score
          // strong=5, adequate/good=4, partial=3, weak/no_evidence=0
          let score = 0;
          const strength = match.evidence_strength?.toLowerCase() || '';
          
          if (strength === 'strong') {
            score = 5;
          } else if (strength === 'adequate' || strength === 'good') {
            score = 4;
          } else if (strength === 'partial') {
            // Partial evidence = score 3 (allowed to proceed)
            score = 3;
          } else if (strength === 'weak') {
            // Weak evidence = score 0, requires override
            score = 0;
          } else {
            // "no_evidence" or any other = score 0, requires override
            score = 0;
          }
          
          autoScores[match.competency_id] = score;
          
          // Auto-fill evidence notes - show warning only for weak/no_evidence
          if (strength === 'no_evidence' || strength === 'weak') {
            autoNotes[match.competency_id] = `⚠️ INSUFFICIENT EVIDENCE: ${match.evidence_provided || match.evidence_summary || 'No evidence found. Requires override.'}`;
          } else {
            autoNotes[match.competency_id] = match.evidence_provided || match.evidence_summary || "";
          }
        });
        
        // Update state with auto-populated scores and notes
        setScores(prev => ({ ...prev, ...autoScores }));
        setScoreNotes(prev => ({ ...prev, ...autoNotes }));
        
        // Count how many have NO evidence (weak or no_evidence) - only these block workflow
        const blockedCount = aiRecommendation.competency_matches.filter(m => 
          ['weak', 'no_evidence', ''].includes(m.evidence_strength?.toLowerCase() || '')
        ).length;
        
        if (blockedCount > 0) {
          toast.warning(`${blockedCount} competencies have no evidence - workflow will be blocked`);
        } else {
          toast.success(`Auto-filled ${Object.keys(autoScores).length} competency scores from Credentia analysis`);
        }
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveScores = async () => {
    if (Object.keys(scores).length === 0) {
      toast.error("Please score at least one competency");
      return;
    }
    
    setSavingScores(true);
    try {
      const scoresArray = Object.entries(scores).map(([competency_id, score]) => ({
        competency_id,
        assessor_score: score,
        assessor_notes: scoreNotes[competency_id] || "",
      }));
      await api.post(`/candidates/${id}/scores`, scoresArray);
      toast.success("Scores saved successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to save scores");
    } finally {
      setSavingScores(false);
    }
  };

  const handleAssessorDecision = async (finalAward, finalCredits, comments) => {
    try {
      await api.post(`/candidates/${id}/assessor-decision`, {
        final_level: selectedLevel,
        final_award: finalAward,
        final_credits: parseInt(finalCredits),
        comments,
      });
      toast.success("Decision submitted for IQA review");
      fetchData();
    } catch (error) {
      toast.error("Failed to submit decision");
    }
  };

  const handleIQADecision = async () => {
    if (!iqaDecision.decision || !iqaDecision.notes) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      await api.post(`/candidates/${id}/iqa-decision`, iqaDecision);
      toast.success(`IQA decision: ${iqaDecision.decision}`);
      setIqaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to submit IQA decision");
    }
  };

  // ==================== INTERVIEW QUESTIONS HANDLERS ====================
  
  const handleGenerateInterviewQuestions = async () => {
    setGeneratingQuestions(true);
    try {
      const response = await api.post(`/candidates/${id}/generate-interview-questions`);
      toast.success(`Generated ${response.data.count} interview questions`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate interview questions");
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewSchedule.slot_1 || !interviewSchedule.slot_2 || !interviewSchedule.slot_3) {
      toast.error("Please provide all 3 time slot options");
      return;
    }
    if (!interviewSchedule.meeting_link) {
      toast.error("Please provide a meeting link");
      return;
    }
    if (!interviewSchedule.expert_email) {
      toast.error("Please provide the Industrial Expert's email");
      return;
    }

    try {
      await api.post(`/candidates/${id}/schedule-interview`, interviewSchedule);
      toast.success("Interview scheduled! Emails sent to expert and candidate.");
      setScheduleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule interview");
    }
  };

  const handleSendWrittenInterview = async () => {
    if (!writtenInterviewData.deadline) {
      toast.error("Please set a deadline for the candidate");
      return;
    }
    if (!writtenInterviewData.assessor_email) {
      toast.error("Please provide the external assessor's email");
      return;
    }
    if (!writtenInterviewData.assessor_name) {
      toast.error("Please provide the assessor's name");
      return;
    }

    setSendingWrittenInterview(true);
    try {
      await api.post(`/candidates/${id}/send-written-interview`, writtenInterviewData);
      toast.success("Written interview sent! Candidate will receive an email with instructions.");
      setWrittenInterviewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send written interview");
    } finally {
      setSendingWrittenInterview(false);
    }
  };

  const openResendInterviewDialog = (target /* "candidate" | "assessor" */) => {
    const wi = candidate?.written_interview;
    if (!wi) return;
    setResendDialog({
      open: true,
      target,
      // Pre-fill with the current deadline so the admin only has to touch
      // it when they actually want to extend.
      deadline: wi.deadline || "",
    });
  };

  const handleResendInterviewLink = async () => {
    const { target, deadline } = resendDialog;
    const label = target === "assessor" ? "external assessor" : "candidate";
    setResendingInterview(true);
    try {
      const body = { target };
      if (deadline) body.deadline = deadline;
      const r = await api.post(`/candidates/${id}/written-interview/resend`, body);
      toast.success(r.data?.message || `Fresh interview link sent to the ${label}.`);
      setResendDialog({ open: false, target: "candidate", deadline: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend interview link");
    } finally {
      setResendingInterview(false);
    }
  };

  const handleRequestMoreInfo = async () => {
    if (!requestInfoMessage.trim()) {
      toast.error("Please describe what information is needed");
      return;
    }
    setSendingInfoRequest(true);
    try {
      const items = requestInfoItems.split("\n").filter(i => i.trim());
      await api.post(`/candidates/${id}/request-info`, {
        message: requestInfoMessage,
        missing_items: items,
      });
      toast.success("Information request sent to candidate via email");
      setRequestInfoDialogOpen(false);
      setRequestInfoMessage("");
      setRequestInfoItems("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    } finally {
      setSendingInfoRequest(false);
    }
  };

  const handleSubmitInterviewResults = async () => {
    const questions = candidate?.interview_questions || [];
    const results = questions.map(q => ({
      question_id: q.question_id,
      result: questionResults[q.question_id]?.result || null,
      remarks: questionResults[q.question_id]?.remarks || ""
    }));

    // Check if all questions are answered
    const unanswered = results.filter(r => !r.result);
    if (unanswered.length > 0) {
      toast.error(`Please mark Pass/Fail for all ${unanswered.length} remaining questions`);
      return;
    }

    try {
      const response = await api.post(`/candidates/${id}/interview-results`, {
        results,
        expert_feedback: expertFeedback,
        iqa_remarks: iqaInterviewRemarks
      });
      
      if (response.data.overall_result === "passed") {
        toast.success("All questions passed! Candidate can now be approved.");
      } else {
        toast.warning(`Interview result: ${response.data.passed_count}/${response.data.failed_count + response.data.passed_count} passed. Candidate may appeal.`);
      }
      setResultsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit interview results");
    }
  };

  const handleDecideAppeal = async (decision) => {
    try {
      await api.post(`/candidates/${id}/appeal/decide`, {
        decision,
        remarks: iqaInterviewRemarks
      });
      toast.success(`Appeal ${decision}. ${decision === 'approved' ? 'Candidate will be re-interviewed.' : 'Application rejected.'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process appeal");
    }
  };

  const handleGenerateReports = async () => {
    setGeneratingReports(true);
    try {
      await api.post(`/candidates/${id}/generate-reports`);
      toast.success("Reports generated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate reports");
    } finally {
      setGeneratingReports(false);
    }
  };

  const handleIssueReportIds = async () => {
    setIssuingReportIds(true);
    try {
      const r = await api.post(`/candidates/${id}/issue-report-ids`);
      const ipcb = r.data?.ipcb_report_id;
      const inst = r.data?.institution_report_id;
      if (r.data?.minted) {
        toast.success(`Permanent Report IDs issued: ${ipcb} / ${inst}`);
      } else {
        toast.info("This candidate already has permanent Report IDs.");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to issue Report IDs");
    } finally {
      setIssuingReportIds(false);
    }
  };

  // Trigger the browser print dialog with only ONE report section visible.
  // Sets `data-print-scope` on <html> which our @media print CSS uses to
  // hide every report-section EXCEPT the one matching.
  const triggerScopedPrint = (scope /* "ipcb" | "institution" | "all" */) => {
    const root = document.documentElement;
    root.setAttribute("data-print-scope", scope);
    setTimeout(() => {
      try {
        window.print();
      } catch (_) {
        toast.error("Press Ctrl+P or ⌘+P to save as PDF.");
      } finally {
        // Clear the scope shortly after the print dialog opens so the
        // on-screen view is restored regardless of whether the user prints
        // or cancels. afterprint fires reliably in Chromium/Firefox/Safari.
        const clear = () => root.removeAttribute("data-print-scope");
        window.addEventListener("afterprint", clear, { once: true });
        setTimeout(clear, 15000);
      }
    }, 150);
  };

  // Single download path used by all three buttons (IPCB-only, Inst-only,
  // combined). Tries the backend's vector-quality PDF endpoint with the
  // requested `kind`; on 503 (deploy image missing WeasyPrint system
  // libs) it falls back to the browser's native print-as-PDF dialog —
  // scoped to the matching report section so the user gets exactly the
  // PDF they asked for.
  const handleDownloadPDF = async (kind = "combined") => {
    const labels = {
      ipcb: "Candidate Evaluation Report (IPCB)",
      institution: "Final RPL Assessment Summary",
      combined: "Combined Credential Report",
    };
    setDownloadingPdf(true);
    try {
      const response = await api.get(
        `/candidates/${id}/report.pdf?kind=${kind}`,
        { responseType: "blob", timeout: 60000 }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (candidate.full_name || candidate.candidate_id)
        .replace(/[^\w\-]+/g, "_");
      const suffix = kind === "ipcb"
        ? "IPCB_Evaluation"
        : kind === "institution"
          ? "RPL_Summary"
          : "RPL_Reports";
      link.download = `${safeName}_${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${labels[kind]} downloaded.`);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 503) {
        // Browser-side vector fallback. The print dialog opens with ONLY
        // the requested section visible.
        toast.info(
          `Opening print dialog for "${labels[kind]}". Choose 'Save as PDF' as the destination.`,
          { duration: 6000 }
        );
        triggerScopedPrint(kind);
      } else {
        console.error("PDF generation error:", error);
        const detail = error?.response?.data?.detail || error?.message || "Unknown error";
        toast.error(`Failed to generate PDF: ${detail}`);
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Option B — open the browser print dialog with friendly guidance. The
  // resulting PDF (via "Save as PDF" destination) is true vector — sharper
  // text, perfectly-scannable QR, tiny file size. We show a one-time hint
  // so the user knows to pick "Save as PDF" in the printer dropdown.
  const handlePrintPdf = () => {
    const SEEN_KEY = "credentia.printHintSeenV1";
    const seen = typeof localStorage !== "undefined" && localStorage.getItem(SEEN_KEY);
    if (seen) {
      try { window.print(); } catch { toast.error("Print failed. Press Ctrl+P or ⌘+P."); }
      return;
    }
    setPrintHintOpen(true);
  };

  const confirmPrintHint = (rememberChoice) => {
    if (rememberChoice && typeof localStorage !== "undefined") {
      localStorage.setItem("credentia.printHintSeenV1", "1");
    }
    setPrintHintOpen(false);
    // tiny defer so the modal animates out before native print dialog opens
    setTimeout(() => {
      try { window.print(); } catch { toast.error("Print failed. Press Ctrl+P or ⌘+P."); }
    }, 150);
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleAdminOverride = async () => {
    // Check if all blocked competencies have remarks
    const missingComps = getMissingCompetencyDetails();
    const unreviewedComps = missingComps.filter(comp => 
      !competencyOverrideRemarks[comp.competency_id] || 
      competencyOverrideRemarks[comp.competency_id].trim() === ""
    );
    
    if (unreviewedComps.length > 0) {
      toast.error(`Please provide remarks for all ${unreviewedComps.length} blocked competencies before override`);
      return;
    }
    
    // Check if all blocked competencies have scores assigned
    const unscoredComps = missingComps.filter(comp => 
      competencyOverrideScores[comp.competency_id] === undefined ||
      competencyOverrideScores[comp.competency_id] === null
    );
    
    if (unscoredComps.length > 0) {
      toast.error(`Please assign scores for all ${unscoredComps.length} blocked competencies before override`);
      return;
    }
    
    if (!adminOverridePassword) {
      toast.error("Please enter the admin password");
      return;
    }
    if (!adminOverrideJustification || adminOverrideJustification.trim().length < 20) {
      toast.error("Global justification must be at least 20 characters");
      return;
    }
    
    setSubmittingOverride(true);
    try {
      // Prepare individual competency overrides
      const competencyOverrides = missingComps.map(comp => ({
        competency_id: comp.competency_id,
        unit_code: comp.unit_code,
        title: comp.title,
        remark: competencyOverrideRemarks[comp.competency_id]?.trim() || "",
        override_score: competencyOverrideScores[comp.competency_id] || 0,
      }));
      
      await api.post(`/candidates/${id}/admin-override`, {
        password: adminOverridePassword,
        justification: adminOverrideJustification.trim(),
        competency_overrides: competencyOverrides,
      });
      
      // Update scores and notes with override data
      const newScores = { ...scores };
      const newNotes = { ...scoreNotes };
      competencyOverrides.forEach(override => {
        newScores[override.competency_id] = override.override_score;
        newNotes[override.competency_id] = `[OVERRIDE] ${override.remark}`;
      });
      setScores(newScores);
      setScoreNotes(newNotes);
      
      toast.success("Admin override applied successfully. Workflow unblocked.");
      setAdminOverrideDialogOpen(false);
      setAdminOverridePassword("");
      setAdminOverrideJustification("");
      setCompetencyOverrideRemarks({});
      setCompetencyOverrideScores({});
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid admin password");
      } else {
        toast.error(error.response?.data?.detail || "Failed to apply override");
      }
    } finally {
      setSubmittingOverride(false);
    }
  };

  // Check if all blocked competencies have remarks and scores for override
  const allBlockedCompetenciesReviewed = () => {
    const missingComps = getMissingCompetencyDetails();
    if (missingComps.length === 0) return true;
    
    return missingComps.every(comp => 
      competencyOverrideRemarks[comp.competency_id]?.trim() &&
      competencyOverrideScores[comp.competency_id] !== undefined &&
      competencyOverrideScores[comp.competency_id] !== null
    );
  };

  // Count reviewed competencies
  const getReviewedCount = () => {
    const missingComps = getMissingCompetencyDetails();
    return missingComps.filter(comp => 
      competencyOverrideRemarks[comp.competency_id]?.trim() &&
      competencyOverrideScores[comp.competency_id] !== undefined
    ).length;
  };

  // Check if workflow is blocked (no admin override applied)
  const isWorkflowBlocked = candidate?.workflow_blocked && !candidate?.admin_override;
  
  // Get missing competency details for display
  const getMissingCompetencyDetails = () => {
    if (!candidate?.missing_competencies || candidate.missing_competencies.length === 0) {
      return [];
    }
    return candidate.missing_competencies.map(compId => {
      const comp = competencies.find(c => c.competency_id === compId);
      return comp || { competency_id: compId, title: "Unknown Competency", unit_code: "N/A" };
    });
  };

  const canScore = candidate && ["documents_uploaded", "ai_review", "assessor_review"].includes(candidate.case_status) && !isWorkflowBlocked;
  const canSubmitDecision = candidate && candidate.case_status === "assessor_review" && Object.keys(scores).length > 0 && !isWorkflowBlocked;
  const canDoIQA = candidate && candidate.case_status === "iqa_review";
  const canGenerateReports = candidate && candidate.case_status === "approved";

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!candidate) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Candidate not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const statusInfo = getStatusInfo(candidate.case_status);
  const aiRec = candidate.ai_recommendation;
  // Filter competencies to only show those from the analyzed framework + level
  const levelCompetencies = competencies.filter(c => {
    if (c.qualification_level !== selectedLevel) return false;
    // If a framework was used during analysis, only show competencies from that framework
    const analysisFrameworkId = candidate?.analysis_framework_id;
    if (analysisFrameworkId) {
      return c.framework_id === analysisFrameworkId;
    }
    // If no framework selected during analysis, show competencies that match the AI's competency_matches
    const aiMatches = candidate?.ai_recommendation?.competency_matches;
    if (aiMatches && aiMatches.length > 0) {
      const matchedIds = new Set(aiMatches.map(m => m.competency_id));
      return matchedIds.has(c.competency_id);
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="candidate-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/candidates")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Candidate Overview */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  {/* Candidate Photo */}
                  <div className="relative">
                    {candidate.photo_url ? (
                      <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-slate-200">
                        <img
                          src={getPhotoUrl(candidate.photo_url)}
                          alt={candidate.full_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-slate-600">
                          {candidate.full_name?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                      data-testid="upload-photo-btn"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                    </button>
                    <input
                      type="file"
                      ref={photoInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                  </div>
                  <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="candidate-name">
                      {candidate.full_name}
                    </h1>
                    <p className="text-slate-500">{candidate.current_role} at {candidate.current_employer || "N/A"}</p>
                    <Badge className={`mt-2 ${statusInfo.color}`} data-testid="candidate-status">
                      {statusInfo.label}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  
                  {/* Framework & Level Selection before Analysis */}
                  {(candidate.case_status === "documents_uploaded" || (aiRec && candidate.case_status !== "approved" && candidate.case_status !== "report_generated")) && (
                    <div className="flex items-center gap-3 mr-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600 whitespace-nowrap">Framework:</Label>
                        <Select 
                          value={selectedFramework || "all"} 
                          onValueChange={(v) => {
                            setSelectedFramework(v === "all" ? "" : v);
                            // Reset level when framework changes — different
                            // frameworks have different level lists.
                            setSelectedLevel("");
                          }}
                          data-testid="assessment-framework-select"
                        >
                          <SelectTrigger className="w-[180px] h-9">
                            <SelectValue placeholder="All competencies" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Competencies</SelectItem>
                            {frameworks.map(fw => (
                              <SelectItem key={fw.framework_id} value={fw.framework_id}>
                                {fw.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600 whitespace-nowrap">Level:</Label>
                        <Select 
                          value={selectedLevel} 
                          onValueChange={setSelectedLevel}
                          data-testid="assessment-level-select"
                        >
                          <SelectTrigger className="w-[160px] h-9">
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            {(() => {
                              // Pull the level list from the chosen framework.
                              // When no framework is selected, fall back to
                              // the historical 4 so the UI never collapses.
                              const fw = frameworks.find((f) => f.framework_id === selectedFramework);
                              const levels = fw?.levels?.length
                                ? fw.levels
                                : [
                                    { code: "diploma",  name: "Diploma",  numeric_rank: 4 },
                                    { code: "bachelor", name: "Bachelor", numeric_rank: 6 },
                                    { code: "master",   name: "Master",   numeric_rank: 7 },
                                    { code: "doctoral", name: "Doctoral", numeric_rank: 8 },
                                  ];
                              return levels.map((lvl) => (
                                <SelectItem key={lvl.code} value={lvl.code}>
                                  {lvl.name}
                                </SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  {candidate.case_status === "documents_uploaded" && (
                    <Button onClick={handleRunAnalysis} disabled={analyzing} data-testid="run-analysis-btn">
                      {analyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4 mr-2" />
                          Analyze with Credentia AI
                        </>
                      )}
                    </Button>
                  )}
                  {aiRec && candidate.case_status !== "approved" && candidate.case_status !== "report_generated" && (
                    <Button 
                      variant="outline" 
                      onClick={handleRunAnalysis} 
                      disabled={analyzing} 
                      data-testid="reanalyze-btn"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Re-analyzing...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4 mr-2" />
                          Re-analyze with Credentia AI
                        </>
                      )}
                    </Button>
                  )}
                  {canGenerateReports && (
                    <Button onClick={handleGenerateReports} disabled={generatingReports} data-testid="generate-reports-btn">
                      {generatingReports ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileCheck className="w-4 h-4 mr-2" />
                          Generate Reports
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{candidate.email}</span>
                </div>
                {candidate.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{candidate.phone}</span>
                  </div>
                )}
                {candidate.years_experience && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{candidate.years_experience} years experience</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">Created {formatDate(candidate.created_at)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Recommendation Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-500">Credentia AI Recommendation</CardTitle>
            </CardHeader>
            <CardContent>
              {aiRec ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Recommended Level</p>
                    <Badge className={getLevelInfo(aiRec.recommended_level).color}>
                      {getLevelInfo(aiRec.recommended_level).label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Primary Award</p>
                    <p className="font-medium text-slate-900 text-sm">{aiRec.primary_award}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-2">AI Confidence</p>
                    <div className="flex items-center gap-2">
                      <Progress value={aiRec.confidence_score} className="h-2 flex-1" />
                      <span className="text-sm font-medium text-slate-900">{aiRec.confidence_score}%</span>
                    </div>
                  </div>
                  {candidate.confidence_data && (
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Evidence-Based Confidence</p>
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 flex-1 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              candidate.confidence_data.calculated >= 80 ? "bg-green-500" :
                              candidate.confidence_data.calculated >= 60 ? "bg-blue-500" :
                              candidate.confidence_data.calculated >= 40 ? "bg-amber-500" : "bg-red-500"
                            }`}
                            style={{ width: `${candidate.confidence_data.calculated}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-blue-700">{candidate.confidence_data.calculated}%</span>
                      </div>
                    </div>
                  )}
                  {candidate.analysis_run_number > 1 && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-blue-600 flex items-center gap-1">
                        <RotateCcw className="w-3 h-3" />
                        Analysis run #{candidate.analysis_run_number}
                      </p>
                    </div>
                  )}
                  {aiRec.ai_generated && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Powered by GPT-5.2
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Brain className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Run Credentia AI analysis to get recommendations</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Insights Row - Confidence, Gap Report, Comparison */}
        {aiRec && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="ai-insights-row">
            {/* Confidence Calibration */}
            <ConfidencePanel
              confidenceData={candidate.confidence_data}
              aiConfidence={aiRec.confidence_score}
            />

            {/* Gap Report */}
            <div className="lg:col-span-2">
              <GapReportPanel gapReport={candidate.gap_report} />
            </div>
          </div>
        )}

        {/* Re-Analysis Comparison */}
        {candidate?.analysis_comparison && (
          <AnalysisComparisonPanel comparison={candidate.analysis_comparison} />
        )}

        {/* Workflow Blocked Alert */}
        {isWorkflowBlocked && (
          <Card className="border-red-300 bg-red-50" data-testid="workflow-blocked-alert">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">
                    Workflow Blocked - Insufficient Evidence
                  </h3>
                  <p className="text-red-700 mb-4">
                    The Credentia analysis has determined that not all mandatory competencies have sufficient evidence. 
                    The assessment workflow is blocked until additional documents are uploaded or an admin override is applied.
                  </p>
                  
                  {/* Evidence Coverage */}
                  <div className="mb-4 p-3 bg-white rounded-lg border border-red-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Evidence Coverage</span>
                      <span className="text-sm font-bold text-red-600">{candidate.evidence_coverage || 0}%</span>
                    </div>
                    <Progress value={candidate.evidence_coverage || 0} className="h-2" />
                    <p className="text-xs text-slate-500 mt-1">100% coverage required to proceed</p>
                  </div>
                  
                  {/* Missing Competencies List with Individual Override Fields */}
                  {getMissingCompetencyDetails().length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-red-800">
                          Review Required for ({getMissingCompetencyDetails().length}) Blocked Competencies:
                        </h4>
                        <Badge variant={allBlockedCompetenciesReviewed() ? "default" : "secondary"} className="text-xs">
                          {getReviewedCount()}/{getMissingCompetencyDetails().length} Reviewed
                        </Badge>
                      </div>
                      <p className="text-xs text-red-600 mb-3">
                        For each competency below, provide a remark explaining alternative evidence gathered (e.g., interview, verbal demonstration) and assign a score.
                      </p>
                      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                        {getMissingCompetencyDetails().map((comp) => {
                          const hasRemark = competencyOverrideRemarks[comp.competency_id]?.trim();
                          const hasScore = competencyOverrideScores[comp.competency_id] !== undefined;
                          const isReviewed = hasRemark && hasScore;
                          
                          return (
                            <div 
                              key={comp.competency_id} 
                              className={`p-3 rounded-lg border ${isReviewed ? 'bg-green-50 border-green-200' : 'bg-white border-red-200'}`}
                            >
                              <div className="flex items-start gap-2 mb-2">
                                {isReviewed ? (
                                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-mono text-slate-500">{comp.unit_code}</span>
                                  <p className="text-sm font-medium text-slate-700">{comp.title}</p>
                                </div>
                              </div>
                              
                              {/* Remark Input */}
                              <div className="mt-2">
                                <Label className="text-xs text-slate-600">
                                  Alternative Evidence / Action Taken *
                                </Label>
                                <Textarea
                                  value={competencyOverrideRemarks[comp.competency_id] || ""}
                                  onChange={(e) => setCompetencyOverrideRemarks(prev => ({
                                    ...prev,
                                    [comp.competency_id]: e.target.value
                                  }))}
                                  placeholder="e.g., Candidate was interviewed on [date] and demonstrated understanding of..."
                                  rows={2}
                                  className="mt-1 text-sm"
                                  data-testid={`remark-${comp.competency_id}`}
                                />
                              </div>
                              
                              {/* Score Selection */}
                              <div className="mt-2">
                                <Label className="text-xs text-slate-600">
                                  Override Score *
                                </Label>
                                <div className="flex gap-1 mt-1">
                                  {[0, 1, 2, 3, 4, 5].map((score) => (
                                    <Button
                                      key={score}
                                      type="button"
                                      size="sm"
                                      variant={competencyOverrideScores[comp.competency_id] === score ? "default" : "outline"}
                                      className={`w-8 h-8 p-0 text-xs ${
                                        competencyOverrideScores[comp.competency_id] === score 
                                          ? score === 0 ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
                                          : ''
                                      }`}
                                      onClick={() => setCompetencyOverrideScores(prev => ({
                                        ...prev,
                                        [comp.competency_id]: score
                                      }))}
                                      data-testid={`score-${comp.competency_id}-${score}`}
                                    >
                                      {score}
                                    </Button>
                                  ))}
                                </div>
                                {competencyOverrideScores[comp.competency_id] === 0 && (
                                  <p className="text-xs text-red-500 mt-1">Score 0 = Interview/Alternative also failed</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      variant="outline" 
                      className="border-red-300 text-red-700 hover:bg-red-100"
                      onClick={() => {
                        const docsTab = document.querySelector('[data-testid="tab-documents"]');
                        if (docsTab) docsTab.click();
                      }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload More Documents
                    </Button>
                    <Dialog open={adminOverrideDialogOpen} onOpenChange={setAdminOverrideDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="destructive"
                          disabled={!allBlockedCompetenciesReviewed()}
                          data-testid="admin-override-btn"
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          {allBlockedCompetenciesReviewed() 
                            ? "Proceed with Admin Override" 
                            : `Review All ${getMissingCompetencyDetails().length} Competencies First`}
                        </Button>
                      </DialogTrigger>
                      <DialogContent aria-describedby="admin-override-desc" className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="w-5 h-5" />
                            Confirm Admin Override
                          </DialogTitle>
                          <DialogDescription id="admin-override-desc">
                            You have reviewed all blocked competencies. Enter admin password to confirm the override.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          {/* Summary of Reviewed Competencies */}
                          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 max-h-40 overflow-y-auto">
                            <p className="text-xs font-semibold text-blue-800 mb-2">
                              Override Summary ({getMissingCompetencyDetails().length} competencies reviewed):
                            </p>
                            {getMissingCompetencyDetails().map(comp => (
                              <div key={comp.competency_id} className="text-xs text-blue-700 mb-1 pb-1 border-b border-blue-100 last:border-0">
                                <span className="font-mono">{comp.unit_code}</span>: Score {competencyOverrideScores[comp.competency_id]}
                                <br />
                                <span className="text-blue-600 italic">"{competencyOverrideRemarks[comp.competency_id]?.substring(0, 50)}..."</span>
                              </div>
                            ))}
                          </div>
                          
                          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-sm text-amber-800">
                              <strong>Warning:</strong> This override will be logged for audit purposes. 
                              All remarks will be saved to Evidence Notes.
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="admin-password">Admin Password *</Label>
                            <Input
                              id="admin-password"
                              type="password"
                              value={adminOverridePassword}
                              onChange={(e) => setAdminOverridePassword(e.target.value)}
                              placeholder="Enter admin password"
                              data-testid="admin-password-input"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="override-justification">Global Justification * (min 20 characters)</Label>
                            <Textarea
                              id="override-justification"
                              value={adminOverrideJustification}
                              onChange={(e) => setAdminOverrideJustification(e.target.value)}
                              placeholder="Overall reason for approving this override..."
                              rows={3}
                              data-testid="override-justification-input"
                            />
                            <p className="text-xs text-slate-500">
                              {adminOverrideJustification.length}/20 characters minimum
                            </p>
                          </div>
                          
                          <div className="flex gap-3 pt-2">
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setAdminOverrideDialogOpen(false);
                                setAdminOverridePassword("");
                                setAdminOverrideJustification("");
                              }}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="destructive"
                              onClick={handleAdminOverride}
                              disabled={submittingOverride || adminOverrideJustification.length < 20 || !adminOverridePassword}
                              className="flex-1"
                              data-testid="submit-override-btn"
                            >
                              {submittingOverride ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                "Confirm Override"
                              )}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Request More Info Button + Info Requests Display */}
        {aiRec && (
          <Card className="border-slate-200" data-testid="info-requests-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-700">Information Requests</CardTitle>
                <Dialog open={requestInfoDialogOpen} onOpenChange={setRequestInfoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="request-info-btn">
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Request More Info
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Request Additional Information</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>What information do you need? <span className="text-red-500">*</span></Label>
                        <Textarea
                          placeholder="Describe what additional documents or information the candidate should provide..."
                          value={requestInfoMessage}
                          onChange={(e) => setRequestInfoMessage(e.target.value)}
                          rows={4}
                          data-testid="request-info-message"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Specific items needed (one per line)</Label>
                        <Textarea
                          placeholder="e.g.:\nSafety training certificate\nEmployment reference from current employer\nProject portfolio for 2023"
                          value={requestInfoItems}
                          onChange={(e) => setRequestInfoItems(e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setRequestInfoDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleRequestMoreInfo} disabled={sendingInfoRequest} data-testid="send-info-request-btn">
                          {sendingInfoRequest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Send to Candidate
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            {candidate.info_requests?.length > 0 && (
              <CardContent className="pt-0 space-y-3">
                {candidate.info_requests.map((req) => (
                  <div key={req.request_id} className={`p-3 rounded-lg border ${req.status === 'responded' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-xs text-slate-500">By {req.requested_by_name} - {formatDate(req.created_at)}</p>
                      <Badge className={req.status === 'responded' ? 'bg-green-600' : 'bg-amber-600'} variant="default">
                        {req.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-800">{req.message}</p>
                    {req.missing_items?.length > 0 && (
                      <ul className="text-xs text-slate-600 list-disc list-inside mt-1">
                        {req.missing_items.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    )}
                    {req.candidate_response && (
                      <div className="mt-2 p-2 bg-white rounded border border-green-200">
                        <p className="text-xs text-green-600 font-medium">Candidate Response:</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.candidate_response}</p>
                        <p className="text-[10px] text-slate-400 mt-1">Responded: {formatDate(req.responded_at)}</p>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}


        {/* Admin Override Applied Notice */}
        {candidate?.admin_override && (
          <Card className="border-amber-300 bg-amber-50" data-testid="override-applied-notice">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Admin Override Applied
                  </p>
                  <p className="text-xs text-amber-600">
                    By {candidate.admin_override.admin_name} on{" "}
                    {formatDateTime(candidate.admin_override.created_at)}
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    <strong>Global Justification:</strong> {candidate.admin_override.justification}
                  </p>
                  
                  {/* Individual Competency Overrides */}
                  {candidate.admin_override.competency_overrides?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs font-semibold text-amber-800 mb-2">
                        Individual Competency Reviews ({candidate.admin_override.competency_overrides.length}):
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {candidate.admin_override.competency_overrides.map((override, idx) => (
                          <div key={idx} className="p-2 bg-white/50 rounded text-xs border border-amber-100">
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-amber-700">{override.unit_code}</span>
                              <Badge variant="outline" className="text-xs">Score: {override.override_score}/5</Badge>
                            </div>
                            <p className="text-amber-600 mt-1 italic">"{override.remark}"</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList>
            <TabsTrigger value="documents" data-testid="tab-documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents ({candidate.documents?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="analysis" data-testid="tab-analysis">
              <Brain className="w-4 h-4 mr-2" />
              Credentia AI Analysis
            </TabsTrigger>
            <TabsTrigger value="scoring" data-testid="tab-scoring">
              <Star className="w-4 h-4 mr-2" />
              Assessor Scoring
            </TabsTrigger>
            <TabsTrigger value="interview" data-testid="tab-interview">
              <MessageSquare className="w-4 h-4 mr-2" />
              Interview
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              <FileCheck className="w-4 h-4 mr-2" />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Uploaded Documents</CardTitle>
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="upload-document-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                  </DialogTrigger>
                  <DialogContent aria-describedby="upload-doc-desc">
                    <DialogHeader>
                      <DialogTitle>Upload Document</DialogTitle>
                      <DialogDescription id="upload-doc-desc">
                        Select a category and upload a document for this candidate.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Document Category</Label>
                        <Select value={uploadCategory} onValueChange={(value) => {
                          setUploadCategory(value);
                          // Clear custom category text when switching away from "Other"
                          if (value !== "Other Supporting Evidence") {
                            setCustomCategoryText("");
                          }
                        }}>
                          <SelectTrigger data-testid="upload-category-select">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {DOCUMENT_CATEGORIES.map((cat) => (
                              <SelectItem key={cat} value={cat}>
                                {cat}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Custom category input - shown when "Other Supporting Evidence" is selected */}
                      {uploadCategory === "Other Supporting Evidence" && (
                        <div className="space-y-2">
                          <Label>Specify Document Type <span className="text-red-500">*</span></Label>
                          <Input
                            type="text"
                            value={customCategoryText}
                            onChange={(e) => setCustomCategoryText(e.target.value)}
                            placeholder="e.g., Training Completion Certificate, Project Report"
                            data-testid="custom-category-input"
                            className="border-amber-300 focus:border-amber-500"
                          />
                          <p className="text-xs text-slate-500">
                            Please describe the type of document you are uploading.
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label>File</Label>
                        <Input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                          data-testid="file-input"
                          disabled={uploading || !uploadCategory || (uploadCategory === "Other Supporting Evidence" && !customCategoryText.trim())}
                        />
                      </div>
                      {uploading && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {candidate.documents?.length > 0 ? (
                  <div className="space-y-3">
                    {candidate.documents.map((doc) => (
                      <div
                        key={doc.document_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        data-testid={`document-${doc.document_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-slate-400" />
                          <div>
                            <p className="font-medium text-slate-900">{doc.file_name}</p>
                            <p className="text-sm text-slate-500">
                              {doc.category}
                              {doc.custom_category && (
                                <span className="text-amber-600 font-medium"> - {doc.custom_category}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {doc.ocr_status}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-600 hover:text-slate-900"
                            onClick={() => window.open(`${API}/documents/${doc.document_id}/view`, "_blank")}
                            data-testid={`view-doc-${doc.document_id}`}
                            title="View (watermarked, in-browser)"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => { window.location.href = `${API}/documents/${doc.document_id}/download`; }}
                            data-testid={`download-doc-${doc.document_id}`}
                            title="Download (watermarked, audit-logged)"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteDocument(doc.document_id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No documents uploaded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Analysis Tab */}
          <TabsContent value="analysis">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Credentia AI Analysis Results</CardTitle>
                  {aiRec && candidate.case_status !== "approved" && candidate.case_status !== "report_generated" && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleRunAnalysis} 
                      disabled={analyzing}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Re-analyzing...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4 mr-2" />
                          Re-analyze
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {aiRec ? (
                  <div className="space-y-6">
                    {/* Recommendation Summary */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-500 mb-1">Recommended Level</p>
                        <Badge className={`text-lg px-3 py-1 ${getLevelInfo(aiRec.recommended_level).color}`}>
                          {getLevelInfo(aiRec.recommended_level).label}
                        </Badge>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-500 mb-1">Primary Award</p>
                        <p className="font-semibold text-slate-900">{aiRec.primary_award}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-500 mb-2">Confidence</p>
                        <div className="flex items-center gap-2">
                          <Progress value={aiRec.confidence_score} className="h-3 flex-1" />
                          <span className="font-bold text-slate-900">{aiRec.confidence_score}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Alternative Awards */}
                    {aiRec.alternative_awards?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-2">Alternative Awards</p>
                        <div className="flex flex-wrap gap-2">
                          {aiRec.alternative_awards.map((award, i) => (
                            <Badge key={i} variant="outline">{award}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Reasoning */}
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-2">AI Reasoning</p>
                      <p className="text-slate-700 bg-slate-50 rounded-lg p-4">{aiRec.reasoning}</p>
                    </div>

                    {/* Competency Matches */}
                    {aiRec.competency_matches?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-3">Competency Evidence Mapping</p>
                        <div className="space-y-2">
                          {aiRec.competency_matches.map((match, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                              <Badge variant="outline" className="mt-0.5">{match.unit_code}</Badge>
                              <div className="flex-1">
                                <p className="text-sm text-slate-700">{match.evidence_summary}</p>
                              </div>
                              <Badge className={`evidence-${match.evidence_strength}`}>
                                {match.evidence_strength}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Extracted Profile */}
                    {aiRec.extracted_profile && (
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-3">Extracted Candidate Profile</p>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(aiRec.extracted_profile).map(([key, value]) => (
                            <div key={key} className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, " ")}</p>
                              <p className="font-medium text-slate-900">
                                {Array.isArray(value) ? value.join(", ") || "None" : 
                                 typeof value === "boolean" ? (value ? "Yes" : "No") : 
                                 value || "N/A"}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No Analysis Yet</h3>
                    <p className="text-slate-500 mb-4">Upload documents and run Credentia AI analysis to get recommendations</p>
                    {candidate.documents?.length > 0 && (
                      <Button onClick={handleRunAnalysis} disabled={analyzing}>
                        {analyzing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Brain className="w-4 h-4 mr-2" />
                            Analyze with Credentia AI
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Document Authentication Panel */}
          {candidate.document_authentication && (
            <Card className={`border-${
              candidate.document_authentication.overall_risk === 'high' ? 'red' :
              candidate.document_authentication.overall_risk === 'medium' ? 'amber' : 'green'
            }-200 bg-${
              candidate.document_authentication.overall_risk === 'high' ? 'red' :
              candidate.document_authentication.overall_risk === 'medium' ? 'amber' : 'green'
            }-50/30 mb-4`} data-testid="doc-authentication-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Document Authentication
                  </CardTitle>
                  <Badge className={
                    candidate.document_authentication.overall_risk === 'high' ? 'bg-red-600' :
                    candidate.document_authentication.overall_risk === 'medium' ? 'bg-amber-600' : 'bg-green-600'
                  }>
                    {candidate.document_authentication.overall_risk?.toUpperCase()} RISK
                  </Badge>
                </div>
                {candidate.document_authentication.summary && (
                  <p className="text-sm text-slate-600 mt-1">{candidate.document_authentication.summary}</p>
                )}
              </CardHeader>
              {candidate.document_authentication.flags?.length > 0 && (
                <CardContent className="pt-0 space-y-2">
                  {candidate.document_authentication.flags.map((flag, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border text-sm ${
                      flag.severity === 'critical' ? 'bg-red-50 border-red-200' :
                      flag.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          flag.severity === 'critical' ? 'text-red-500' :
                          flag.severity === 'warning' ? 'text-amber-500' : 'text-blue-500'
                        }`} />
                        <div>
                          <p className="font-medium text-slate-800">{flag.message}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">{flag.type?.replace(/_/g, " ")}</Badge>
                            <Badge variant="outline" className="text-[10px]">{flag.severity}</Badge>
                          </div>
                          {flag.affected_documents?.length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">Affected: {flag.affected_documents.join(", ")}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}


          {/* Scoring Tab */}
          <TabsContent value="scoring">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>Assessor Scoring Dashboard</CardTitle>
                    <CardDescription>Score candidate evidence against competencies (0-5 scale)</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Selected Award Title Dropdown */}
                    <Select 
                      value={selectedAwardTitle} 
                      onValueChange={(value) => {
                        setSelectedAwardTitle(value);
                        // Update level based on selected award
                        const aiLevel = aiRec?.recommended_level || "bachelor";
                        // Check if the award is from AI or database
                        const dbAward = awards.find(a => a.title === value);
                        if (dbAward) {
                          setSelectedLevel(dbAward.qualification_level);
                        } else {
                          // It's an AI-suggested award, use AI level
                          setSelectedLevel(aiLevel);
                        }
                      }}
                    >
                      <SelectTrigger className="w-[320px]" data-testid="selected-award-dropdown">
                        <SelectValue placeholder="Select Award Title" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* AI Suggested Awards - 1 Primary + 2 Alternatives */}
                        {aiRec && (aiRec.primary_award || aiRec.alternative_awards?.length > 0) && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50">
                              AI Suggested Awards (1 Primary + 2 Alternatives)
                            </div>
                            {aiRec.primary_award && (
                              <SelectItem value={aiRec.primary_award} className="font-medium">
                                ★ {aiRec.primary_award} (Primary)
                              </SelectItem>
                            )}
                            {aiRec.alternative_awards?.slice(0, 2).map((award, index) => (
                              <SelectItem key={`ai-alt-${index}`} value={award}>
                                ○ {award} (Alternative {index + 1})
                              </SelectItem>
                            ))}
                          </>
                        )}
                        
                        {/* Database Awards - Institution's Own Awards */}
                        {(() => {
                          const aiLevel = aiRec?.recommended_level || selectedLevel;
                          const aiAwards = [aiRec?.primary_award, ...(aiRec?.alternative_awards || [])].filter(Boolean);
                          const levelAwards = awards.filter(a => 
                            a.qualification_level === aiLevel && !aiAwards.includes(a.title)
                          );
                          
                          // Show all database awards for the level
                          if (levelAwards.length > 0) {
                            return (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-green-700 bg-green-50 mt-1">
                                  Institution Awards ({aiLevel.charAt(0).toUpperCase() + aiLevel.slice(1)} Level)
                                </div>
                                {levelAwards.map((award) => (
                                  <SelectItem key={award.award_id} value={award.title}>
                                    ◆ {award.title} ({award.total_credits} credits)
                                  </SelectItem>
                                ))}
                              </>
                            );
                          }
                          return null;
                        })()}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={handleSaveScores} disabled={savingScores || !canScore} data-testid="save-scores-btn">
                      {savingScores ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Save Scores
                    </Button>
                    {canSubmitDecision && (
                      <AssessorDecisionDialog
                        awards={awards.filter(a => a.qualification_level === selectedLevel)}
                        selectedLevel={selectedLevel}
                        selectedAwardTitle={selectedAwardTitle}
                        onSubmit={handleAssessorDecision}
                        aiRecommendation={aiRec}
                      />
                    )}
                    {canDoIQA && (
                      <Dialog open={iqaDialogOpen} onOpenChange={setIqaDialogOpen}>
                        <DialogTrigger asChild>
                          <Button data-testid="iqa-decision-btn">IQA Decision</Button>
                        </DialogTrigger>
                        <DialogContent aria-describedby="iqa-dialog-desc" className="max-w-lg">
                          <DialogHeader>
                            <DialogTitle>IQA Verification Decision</DialogTitle>
                            <DialogDescription id="iqa-dialog-desc">
                              Review and verify the assessor's decision.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div className="space-y-2">
                              <Label htmlFor="iqa-decision">Decision *</Label>
                              <Select
                                value={iqaDecision.decision}
                                onValueChange={(v) => setIqaDecision({ ...iqaDecision, decision: v })}
                              >
                                <SelectTrigger data-testid="iqa-decision-select" className="w-full">
                                  <SelectValue placeholder="Select decision" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="approved">Approve</SelectItem>
                                  <SelectItem value="returned">Return for Revision</SelectItem>
                                  <SelectItem value="rejected">Reject</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="iqa-notes">IQA Notes</Label>
                              <Textarea
                                id="iqa-notes"
                                value={iqaDecision.notes}
                                onChange={(e) => setIqaDecision({ ...iqaDecision, notes: e.target.value })}
                                placeholder="Enter verification notes and any observations..."
                                data-testid="iqa-notes-input"
                                rows={4}
                                className="w-full"
                              />
                            </div>
                            <Button onClick={handleIQADecision} className="w-full" data-testid="submit-iqa-btn">
                              Submit IQA Decision
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Workflow Status */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">Assessment Workflow</h4>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <Badge className={candidate.case_status === "documents_uploaded" ? "bg-blue-600" : "bg-slate-300"}>
                      1. Documents
                    </Badge>
                    <span className="text-slate-400">→</span>
                    <Badge className={candidate.case_status === "ai_review" ? "bg-blue-600" : "bg-slate-300"}>
                      2. Credentia Review
                    </Badge>
                    <span className="text-slate-400">→</span>
                    <Badge className={candidate.case_status === "assessor_review" ? "bg-blue-600" : (isWorkflowBlocked ? "bg-red-500" : "bg-slate-300")}>
                      3. Assessor {isWorkflowBlocked && "⚠️"}
                    </Badge>
                    <span className="text-slate-400">→</span>
                    <Badge className={candidate.case_status === "iqa_review" ? "bg-blue-600" : "bg-slate-300"}>
                      4. IQA
                    </Badge>
                    <span className="text-slate-400">→</span>
                    <Badge className={candidate.case_status === "approved" ? "bg-green-600" : "bg-slate-300"}>
                      5. Approved
                    </Badge>
                  </div>
                </div>

                {/* Workflow Blocked Notice in Scoring Tab */}
                {isWorkflowBlocked && (
                  <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200" data-testid="scoring-blocked-notice">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-800">Scoring Disabled - Workflow Blocked</h4>
                        <p className="text-sm text-red-700 mt-1">
                          Evidence coverage is at <strong>{candidate.evidence_coverage || 0}%</strong>. 
                          Scoring and decision submission are disabled until 100% evidence coverage is achieved 
                          or an admin override is applied.
                        </p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                          View Blocked Alert Above
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected Award Display */}
                {selectedAwardTitle && (
                  <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-medium text-amber-900 mb-1">Selected Award Title</h4>
                    <p className="text-lg font-semibold text-amber-800">{selectedAwardTitle}</p>
                    <p className="text-sm text-amber-600 mt-1">
                      Level: {QUALIFICATION_LEVELS.find(l => l.value === selectedLevel)?.label || selectedLevel}
                    </p>
                  </div>
                )}

                {/* AI Auto-fill Notice */}
                {aiRec?.competency_matches && Object.keys(scores).length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-blue-600" />
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Scores auto-filled by Credentia AI</span> based on evidence strength analysis. You can adjust scores manually if needed.
                    </p>
                  </div>
                )}

                {/* Empty State - No AI Analysis yet */}
                {!aiRec && !candidate.documents?.length && (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No Data Available</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                      Upload documents and run Credentia AI analysis first. Then competencies will appear here for scoring.
                    </p>
                  </div>
                )}

                {/* Empty State - Documents uploaded but no AI analysis */}
                {!aiRec && candidate.documents?.length > 0 && (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Waiting for AI Analysis</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                      Documents have been uploaded. Run Credentia AI analysis to generate competency recommendations and enable scoring.
                    </p>
                  </div>
                )}

                {/* Competencies List - Only show when AI analysis exists */}
                {aiRec && levelCompetencies.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-slate-500">
                        Showing {levelCompetencies.length} competencies for{" "}
                        <span className="font-medium">{selectedAwardTitle || QUALIFICATION_LEVELS.find(l => l.value === selectedLevel)?.label}</span>
                      </p>
                      <p className="text-sm text-slate-500">
                        Scored: {Object.keys(scores).filter(k => levelCompetencies.some(c => c.competency_id === k)).length} / {levelCompetencies.length}
                      </p>
                    </div>
                    {levelCompetencies.map((comp) => (
                      <div
                        key={comp.competency_id}
                        className={`border rounded-lg p-4 transition-colors ${
                          scores[comp.competency_id] !== undefined 
                            ? "border-green-200 bg-green-50/50" 
                            : "border-slate-200"
                        }`}
                        data-testid={`competency-${comp.competency_id}`}
                      >
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge variant="outline">{comp.unit_code}</Badge>
                              <Badge>{comp.credit_hours} credits</Badge>
                              {scores[comp.competency_id] !== undefined && (
                                <Badge className={SCORE_DESCRIPTIONS[scores[comp.competency_id]]?.color}>
                                  Score: {scores[comp.competency_id]}/5
                                </Badge>
                              )}
                            </div>
                            <h4 className="font-medium text-slate-900">{comp.title}</h4>
                            <p className="text-sm text-slate-500 mt-1">{comp.description}</p>
                            <p className="text-sm text-slate-400 mt-1">
                              <span className="font-medium">Evidence Required:</span> {comp.evidence_required}
                            </p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            {[0, 1, 2, 3, 4, 5].map((score) => (
                              <button
                                key={score}
                                onClick={() => setScores({ ...scores, [comp.competency_id]: score })}
                                disabled={!canScore}
                                className={`w-9 h-9 rounded-full text-sm font-medium transition-all ${
                                  scores[comp.competency_id] === score
                                    ? SCORE_DESCRIPTIONS[score].color + " text-white shadow-md"
                                    : canScore 
                                      ? "bg-slate-100 text-slate-600 hover:bg-slate-200" 
                                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                                }`}
                                data-testid={`score-${comp.competency_id}-${score}`}
                              >
                                {score}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Evidence Notes</Label>
                          <Textarea
                            placeholder="Notes about evidence provided for this competency..."
                            value={scoreNotes[comp.competency_id] || ""}
                            onChange={(e) => setScoreNotes({ ...scoreNotes, [comp.competency_id]: e.target.value })}
                            className="mt-1"
                            rows={2}
                            disabled={!canScore}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No Competencies for {QUALIFICATION_LEVELS.find(l => l.value === selectedLevel)?.label}</h3>
                    <p className="text-slate-500 mb-4">Please add competencies in the Competency Frameworks page first</p>
                    <Button variant="outline" onClick={() => navigate("/competencies")}>
                      Go to Competency Frameworks
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                <CardTitle>Generated Reports</CardTitle>
                {candidate.report_data && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleDownloadPDF("ipcb")}
                      disabled={downloadingPdf}
                      data-testid="download-pdf-ipcb-btn"
                    >
                      {downloadingPdf ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download IPCB Evaluation
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDownloadPDF("institution")}
                      disabled={downloadingPdf}
                      data-testid="download-pdf-institution-btn"
                    >
                      {downloadingPdf ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download RPL Summary
                    </Button>
                    <Button
                      variant="default"
                      onClick={handlePrintPdf}
                      data-testid="print-reports-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print / Save Both
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {candidate.report_data ? (
                  <div className="space-y-8 print-area" id="report-print-area">
                    {/* Legacy case — no permanent Report ID yet */}
                    {!candidate.ipcb_report_id || !candidate.institution_report_id ? (
                      <div
                        className="no-print rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col md:flex-row md:items-center gap-3"
                        data-testid="issue-report-ids-banner"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-900">
                            This case was finalised before permanent Report IDs were introduced.
                          </p>
                          <p className="text-xs text-amber-800 mt-1">
                            Click below to mint permanent IDs so the QR codes,
                            the candidate's <code>/my-application</code> dashboard,
                            and the public <code>/verify</code> page all start working
                            for this report. This is safe to re-run and does not
                            change the AI analysis or send any emails.
                          </p>
                        </div>
                        <Button
                          onClick={handleIssueReportIds}
                          disabled={issuingReportIds}
                          data-testid="issue-report-ids-btn"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          {issuingReportIds ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : null}
                          Issue Permanent Report ID
                        </Button>
                      </div>
                    ) : null}
                    {/* IPCB Evaluation Report - New Format */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-report-section="ipcb">
                      <IPCBReport
                        ref={ipcbReportRef}
                        candidate={candidate}
                        ipcb={ipcbData}
                        institution={institutionData}
                        documents={candidate.documents || []}
                        scores={candidate.scores || []}
                        competencies={levelCompetencies}
                        aiRecommendation={candidate.ai_recommendation}
                      />
                    </div>

                    {/* Institution Report - New Format */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-report-section="institution">
                      <InstitutionReport
                        ref={institutionReportRef}
                        candidate={candidate}
                        institution={institutionData}
                        competencies={levelCompetencies}
                        scores={candidate.scores || []}
                        aiRecommendation={candidate.ai_recommendation}
                        assessorName="RPL Coordinator"
                      />
                    </div>

                    {/* Digital Badges panel — appears only after a final
                        report exists. Phase 1 ships Competency Badges. */}
                    <div className="no-print" data-testid="badges-section">
                      {/* Quick admin tool to set or correct the candidate's
                          framework & qualification level — without this,
                          badges cannot be generated for legacy cases. */}
                      {(user?.role === "super_admin" || user?.role === "admin" || user?.role === "assessor") && (
                        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-3 text-sm">
                          <div className="text-slate-700">
                            <span className="text-slate-500">Framework:</span>{" "}
                            <strong>{candidate.framework_name || candidate.framework_id || "Not set"}</strong>
                            <span className="mx-2 text-slate-300">·</span>
                            <span className="text-slate-500">Level:</span>{" "}
                            <strong>{candidate.qualification_level_name || candidate.qualification_level_code || candidate.final_level || "Not set"}</strong>
                          </div>
                          <CandidateFrameworkLevelEditor
                            candidate={candidate}
                            onSaved={() => fetchData()}
                          />
                        </div>
                      )}
                      <BadgesPanel candidateId={candidate.candidate_id} currentUserRole={user?.role} />
                    </div>
                  </div>
                ) : canGenerateReports ? (
                  <div className="text-center py-12">
                    <FileCheck className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Ready for Report Generation</h3>
                    <p className="text-slate-500 mb-4">The candidate has been approved. Generate the official reports now.</p>
                    <Button onClick={handleGenerateReports} disabled={generatingReports} size="lg" data-testid="generate-reports-btn-main">
                      {generatingReports ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Reports...
                        </>
                      ) : (
                        <>
                          <FileCheck className="w-4 h-4 mr-2" />
                          Generate Both Reports
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Reports Not Available Yet</h3>
                    <p className="text-slate-500 mb-4">Complete the assessment workflow to generate reports:</p>
                    <div className="text-left max-w-md mx-auto space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-5 h-5 ${candidate.documents?.length > 0 ? "text-green-500" : "text-slate-300"}`} />
                        <span className={candidate.documents?.length > 0 ? "text-slate-700" : "text-slate-400"}>Upload candidate documents</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-5 h-5 ${aiRec ? "text-green-500" : "text-slate-300"}`} />
                        <span className={aiRec ? "text-slate-700" : "text-slate-400"}>Run Credentia analysis</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-5 h-5 ${candidate.assessor_scores ? "text-green-500" : "text-slate-300"}`} />
                        <span className={candidate.assessor_scores ? "text-slate-700" : "text-slate-400"}>Complete assessor scoring</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className={`w-5 h-5 ${candidate.case_status === "approved" || candidate.case_status === "report_generated" ? "text-green-500" : "text-slate-300"}`} />
                        <span className={candidate.case_status === "approved" || candidate.case_status === "report_generated" ? "text-slate-700" : "text-slate-400"}>IQA approval</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interview Tab */}
          <TabsContent value="interview">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Interview Questions & Scheduling
                </CardTitle>
                <CardDescription>
                  AI-generated questions for Industrial Expert interview
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Step 1: Generate Questions */}
                {!candidate.interview_questions || candidate.interview_questions.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Generate Interview Questions</h3>
                    <p className="text-slate-500 mb-4 max-w-md mx-auto">
                      AI will generate 10 interview questions based on the candidate's competencies and documents.
                    </p>
                    {candidate.assessor_scores ? (
                      <Button 
                        onClick={handleGenerateInterviewQuestions} 
                        disabled={generatingQuestions}
                        data-testid="generate-questions-btn"
                      >
                        {generatingQuestions ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating Questions...
                          </>
                        ) : (
                          <>
                            <Brain className="w-4 h-4 mr-2" />
                            Generate 10 Interview Questions
                          </>
                        )}
                      </Button>
                    ) : (
                      <p className="text-amber-600 text-sm">Complete Assessor Scoring first to generate questions.</p>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Questions List */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">
                          Interview Questions ({candidate.interview_questions?.length || 0})
                        </h3>
                        <Badge variant={candidate.interview_results ? "default" : "outline"}>
                          {candidate.interview_results ? "Completed" : "Pending Interview"}
                        </Badge>
                      </div>
                      
                      <div className="space-y-3">
                        {candidate.interview_questions?.map((q, index) => (
                          <div 
                            key={q.question_id} 
                            className={`p-4 rounded-lg border ${
                              q.result === 'pass' ? 'bg-green-50 border-green-200' :
                              q.result === 'fail' ? 'bg-red-50 border-red-200' :
                              'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <p className="text-slate-900">{q.question_text}</p>
                                <p className="text-xs text-slate-500 mt-1">Related to: {q.competency_name}</p>
                                {q.modified_by_expert && (
                                  <Badge variant="outline" className="mt-2 text-xs">Modified by Expert</Badge>
                                )}
                                {q.result && (
                                  <div className="mt-2 flex items-center gap-2">
                                    {q.result === 'pass' ? (
                                      <Badge className="bg-green-600">PASS</Badge>
                                    ) : (
                                      <Badge className="bg-red-600">FAIL</Badge>
                                    )}
                                    {q.remarks && <span className="text-sm text-slate-600">{q.remarks}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Interview Schedule Section */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold text-slate-900 mb-4">Interview Schedule</h3>
                      
                      {!candidate.interview_schedule && !candidate.written_interview ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="text-amber-800 mb-3">Choose interview mode:</p>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                              <DialogTrigger asChild>
                                <Button variant="outline" className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="schedule-interview-btn">
                                  <Video className="w-4 h-4 mr-2" />
                                  Schedule Live Interview
                                </Button>
                              </DialogTrigger>
                            <DialogContent className="max-w-lg">
                              <DialogHeader>
                                <DialogTitle>Schedule Interview</DialogTitle>
                                <DialogDescription>
                                  Set 3 time slots for the candidate to choose from
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <div className="space-y-3">
                                  <Label>Time Slot Options</Label>
                                  <Input
                                    placeholder="Option 1: e.g., March 20, 2026 at 10:00 AM"
                                    value={interviewSchedule.slot_1}
                                    onChange={(e) => setInterviewSchedule({...interviewSchedule, slot_1: e.target.value})}
                                  />
                                  <Input
                                    placeholder="Option 2: e.g., March 21, 2026 at 2:00 PM"
                                    value={interviewSchedule.slot_2}
                                    onChange={(e) => setInterviewSchedule({...interviewSchedule, slot_2: e.target.value})}
                                  />
                                  <Input
                                    placeholder="Option 3: e.g., March 22, 2026 at 11:00 AM"
                                    value={interviewSchedule.slot_3}
                                    onChange={(e) => setInterviewSchedule({...interviewSchedule, slot_3: e.target.value})}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Meeting Link</Label>
                                  <Input
                                    placeholder="https://meet.google.com/xxx-xxx-xxx"
                                    value={interviewSchedule.meeting_link}
                                    onChange={(e) => setInterviewSchedule({...interviewSchedule, meeting_link: e.target.value})}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Meeting Platform</Label>
                                  <Select
                                    value={interviewSchedule.meeting_platform}
                                    onValueChange={(v) => setInterviewSchedule({...interviewSchedule, meeting_platform: v})}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="google_meet">Google Meet</SelectItem>
                                      <SelectItem value="zoom">Zoom</SelectItem>
                                      <SelectItem value="teams">Microsoft Teams</SelectItem>
                                      <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="border-t pt-4">
                                  <h4 className="font-medium mb-3">Industrial Expert Details</h4>
                                  <div className="space-y-3">
                                    <Input
                                      placeholder="Expert Name"
                                      value={interviewSchedule.expert_name}
                                      onChange={(e) => setInterviewSchedule({...interviewSchedule, expert_name: e.target.value})}
                                    />
                                    <Input
                                      placeholder="Expert Email"
                                      type="email"
                                      value={interviewSchedule.expert_email}
                                      onChange={(e) => setInterviewSchedule({...interviewSchedule, expert_email: e.target.value})}
                                    />
                                    <Input
                                      placeholder="Expert Organization (optional)"
                                      value={interviewSchedule.expert_organization}
                                      onChange={(e) => setInterviewSchedule({...interviewSchedule, expert_organization: e.target.value})}
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                  <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
                                  <Button onClick={handleScheduleInterview} data-testid="confirm-schedule-btn">
                                    <Send className="w-4 h-4 mr-2" />
                                    Schedule & Send Emails
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                            {/* Written Interview Option */}
                            <Dialog open={writtenInterviewDialogOpen} onOpenChange={setWrittenInterviewDialogOpen}>
                              <DialogTrigger asChild>
                                <Button variant="outline" className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="send-written-interview-btn">
                                  <FileText className="w-4 h-4 mr-2" />
                                  Send Written Interview
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-lg">
                                <DialogHeader>
                                  <DialogTitle>Send Written Interview</DialogTitle>
                                  <DialogDescription>
                                    Candidate will answer 10 questions in writing. External assessor will review and score.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                  <div className="space-y-2">
                                    <Label>Deadline for Candidate <span className="text-red-500">*</span></Label>
                                    <Input
                                      type="date"
                                      value={writtenInterviewData.deadline}
                                      onChange={(e) => setWrittenInterviewData({...writtenInterviewData, deadline: e.target.value})}
                                      data-testid="written-deadline-input"
                                    />
                                    <p className="text-xs text-slate-500">Candidate must submit answers before this date</p>
                                  </div>
                                  <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3">External Assessor Details</h4>
                                    <div className="space-y-3">
                                      <div className="space-y-1">
                                        <Label>Assessor Name <span className="text-red-500">*</span></Label>
                                        <Input
                                          placeholder="e.g., Dr. John Smith"
                                          value={writtenInterviewData.assessor_name}
                                          onChange={(e) => setWrittenInterviewData({...writtenInterviewData, assessor_name: e.target.value})}
                                          data-testid="written-assessor-name"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label>Assessor Email <span className="text-red-500">*</span></Label>
                                        <Input
                                          type="email"
                                          placeholder="assessor@company.com"
                                          value={writtenInterviewData.assessor_email}
                                          onChange={(e) => setWrittenInterviewData({...writtenInterviewData, assessor_email: e.target.value})}
                                          data-testid="written-assessor-email"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label>Organisation (optional)</Label>
                                        <Input
                                          placeholder="Company or institution"
                                          value={writtenInterviewData.assessor_organization}
                                          onChange={(e) => setWrittenInterviewData({...writtenInterviewData, assessor_organization: e.target.value})}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
                                    <p className="font-medium mb-1">How it works:</p>
                                    <ol className="list-decimal list-inside space-y-0.5 text-xs">
                                      <li>Candidate receives email with deadline to answer questions</li>
                                      <li>After submission, assessor receives answers via secure link</li>
                                      <li>Assessor scores Pass/Fail + can send up to 3 clarifications per question</li>
                                      <li>Final scores determine interview outcome</li>
                                    </ol>
                                  </div>
                                  <div className="flex justify-end gap-3 pt-4">
                                    <Button variant="outline" onClick={() => setWrittenInterviewDialogOpen(false)}>Cancel</Button>
                                    <Button onClick={handleSendWrittenInterview} disabled={sendingWrittenInterview} data-testid="confirm-written-interview-btn">
                                      {sendingWrittenInterview ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                                      ) : (
                                        <><Send className="w-4 h-4 mr-2" />Send Written Interview</>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            <strong>Live Interview:</strong> For overseas/in-person candidates needing date scheduling.
                            <strong className="ml-2">Written Interview:</strong> Candidate answers questions in writing, assessor reviews remotely.
                          </p>
                        </div>
                      ) : candidate.written_interview ? (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <h4 className="font-medium text-purple-800">Written Interview</h4>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={
                                candidate.written_interview.status === "completed" ? "bg-green-600" :
                                candidate.written_interview.status === "answers_submitted" ? "bg-blue-600" :
                                candidate.written_interview.status === "clarification_pending" ? "bg-amber-600" :
                                "bg-purple-600"
                              }>
                                {candidate.written_interview.status?.replace(/_/g, " ")}
                              </Badge>
                              {candidate.written_interview.status !== "completed" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openResendInterviewDialog("candidate")}
                                  disabled={resendingInterview}
                                  className="border-purple-300 text-purple-700 hover:bg-purple-100 h-7 px-2 text-xs"
                                  data-testid="resend-candidate-interview-link-btn"
                                  title="Re-send the same interview link with an optional new due date — earlier emails stay valid."
                                >
                                  {resendingInterview ? "Sending…" : "Resend Candidate Link"}
                                </Button>
                              )}
                              {(candidate.written_interview.candidate_answers?.length > 0) && candidate.written_interview.status !== "completed" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openResendInterviewDialog("assessor")}
                                  disabled={resendingInterview}
                                  className="border-purple-300 text-purple-700 hover:bg-purple-100 h-7 px-2 text-xs"
                                  data-testid="resend-assessor-interview-link-btn"
                                  title="Re-send the same scoring link to the external assessor with an optional new due date — earlier emails stay valid."
                                >
                                  {resendingInterview ? "Sending…" : "Resend Assessor Link"}
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Assessor</p>
                              <p className="font-medium">{candidate.written_interview.assessor_name}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Assessor Email</p>
                              <p className="font-medium">{candidate.written_interview.assessor_email}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Deadline</p>
                              <p className="font-medium">{candidate.written_interview.deadline}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Sent</p>
                              <p className="font-medium">{formatDate(candidate.written_interview.sent_at)}</p>
                            </div>
                          </div>
                          {candidate.written_interview.candidate_submitted_at && (
                            <p className="text-xs text-green-600">Candidate submitted answers on {formatDate(candidate.written_interview.candidate_submitted_at)}</p>
                          )}
                          {candidate.written_interview.assessor_scored_at && (
                            <p className="text-xs text-green-600">Assessor completed scoring on {formatDate(candidate.written_interview.assessor_scored_at)}</p>
                          )}

                          {/* Written Answers Q&A Display for Admin */}
                          {candidate.written_interview.candidate_answers?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-purple-200 space-y-3" data-testid="admin-written-answers">
                              <h5 className="text-sm font-medium text-slate-700">Candidate Answers</h5>
                              {candidate.interview_questions?.map((q, idx) => {
                                const answer = candidate.written_interview.candidate_answers?.find(a => a.question_id === q.question_id);
                                const scoreEntry = candidate.written_interview.assessor_scores?.find(s => s.question_id === q.question_id);
                                return (
                                  <div key={q.question_id} className="p-3 bg-white rounded-lg border border-slate-200 text-sm">
                                    <div className="flex items-start gap-2 mb-2">
                                      <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">{idx + 1}</span>
                                      <p className="text-slate-800 font-medium">{q.question_text}</p>
                                    </div>
                                    {answer?.answer_text ? (
                                      <div className="ml-8 p-2 bg-slate-50 rounded border border-slate-100">
                                        <p className="text-slate-700 whitespace-pre-wrap">{answer.answer_text}</p>
                                      </div>
                                    ) : (
                                      <p className="ml-8 text-slate-400 italic text-xs">No answer yet</p>
                                    )}
                                    {scoreEntry?.result && (
                                      <div className="ml-8 mt-2 flex items-center gap-2">
                                        <Badge className={scoreEntry.result === 'pass' ? 'bg-green-600' : 'bg-red-600'}>
                                          {scoreEntry.result.toUpperCase()}
                                        </Badge>
                                        {scoreEntry.remarks && <span className="text-xs text-slate-500">{scoreEntry.remarks}</span>}
                                      </div>
                                    )}
                                    {scoreEntry?.clarifications?.length > 0 && (
                                      <div className="ml-8 mt-2 space-y-1">
                                        {scoreEntry.clarifications.map((c, ci) => (
                                          <div key={ci} className="text-xs p-2 bg-amber-50 rounded border border-amber-100">
                                            <p className="text-amber-700 font-medium">Clarification: {c.question_text}</p>
                                            {c.answer_text ? (
                                              <p className="text-slate-700 mt-0.5">Reply: {c.answer_text}</p>
                                            ) : (
                                              <p className="text-amber-500 italic mt-0.5">Awaiting reply...</p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Expert</p>
                              <p className="font-medium">{candidate.interview_schedule.expert_name}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Expert Email</p>
                              <p className="font-medium">{candidate.interview_schedule.expert_email}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Status</p>
                              <Badge>{candidate.interview_schedule.status}</Badge>
                            </div>
                            <div>
                              <p className="text-slate-500">Meeting Link</p>
                              <a href={candidate.interview_schedule.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {candidate.interview_schedule.meeting_platform || 'Join Meeting'}
                              </a>
                            </div>
                            {candidate.interview_schedule.selected_datetime && (
                              <div className="col-span-2">
                                <p className="text-slate-500">Selected Time</p>
                                <p className="font-medium text-green-700">{candidate.interview_schedule.selected_datetime}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Submit Results Section */}
                    {candidate.interview_schedule && !candidate.interview_results && (
                      <div className="border-t pt-6">
                        <Dialog open={resultsDialogOpen} onOpenChange={setResultsDialogOpen}>
                          <DialogTrigger asChild>
                            <Button className="w-full" data-testid="submit-results-btn">
                              <UserCheck className="w-4 h-4 mr-2" />
                              Record Interview Results
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Interview Results</DialogTitle>
                              <DialogDescription>Mark Pass/Fail for each question based on the interview</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              {candidate.interview_questions?.map((q, index) => (
                                <div key={q.question_id} className="p-3 border rounded-lg">
                                  <p className="font-medium text-sm mb-2">Q{index + 1}: {q.question_text}</p>
                                  <div className="flex items-center gap-4">
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant={questionResults[q.question_id]?.result === 'pass' ? 'default' : 'outline'}
                                        className={questionResults[q.question_id]?.result === 'pass' ? 'bg-green-600' : ''}
                                        onClick={() => setQuestionResults({
                                          ...questionResults,
                                          [q.question_id]: { ...questionResults[q.question_id], result: 'pass' }
                                        })}
                                      >
                                        <CheckCircle className="w-4 h-4 mr-1" /> Pass
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant={questionResults[q.question_id]?.result === 'fail' ? 'default' : 'outline'}
                                        className={questionResults[q.question_id]?.result === 'fail' ? 'bg-red-600' : ''}
                                        onClick={() => setQuestionResults({
                                          ...questionResults,
                                          [q.question_id]: { ...questionResults[q.question_id], result: 'fail' }
                                        })}
                                      >
                                        <XCircle className="w-4 h-4 mr-1" /> Fail
                                      </Button>
                                    </div>
                                    <Input
                                      placeholder="Remarks (optional)"
                                      className="flex-1"
                                      value={questionResults[q.question_id]?.remarks || ''}
                                      onChange={(e) => setQuestionResults({
                                        ...questionResults,
                                        [q.question_id]: { ...questionResults[q.question_id], remarks: e.target.value }
                                      })}
                                    />
                                  </div>
                                </div>
                              ))}
                              <div className="space-y-3 pt-4 border-t">
                                <div>
                                  <Label>Expert Feedback (optional)</Label>
                                  <Textarea
                                    placeholder="Notes from the Industrial Expert..."
                                    value={expertFeedback}
                                    onChange={(e) => setExpertFeedback(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>IQA Remarks (optional)</Label>
                                  <Textarea
                                    placeholder="Your overall assessment..."
                                    value={iqaInterviewRemarks}
                                    onChange={(e) => setIqaInterviewRemarks(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end gap-3">
                                <Button variant="outline" onClick={() => setResultsDialogOpen(false)}>Cancel</Button>
                                <Button onClick={handleSubmitInterviewResults} data-testid="confirm-results-btn">
                                  Submit Results
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}

                    {/* Interview Results Summary */}
                    {candidate.interview_results && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold text-slate-900 mb-4">Interview Results</h3>
                        <div className={`p-4 rounded-lg ${
                          candidate.interview_results.overall_result === 'passed' 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-red-50 border border-red-200'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <Badge className={candidate.interview_results.overall_result === 'passed' ? 'bg-green-600' : 'bg-red-600'}>
                              {candidate.interview_results.overall_result === 'passed' ? 'ALL PASSED' : 'FAILED'}
                            </Badge>
                            <span className="text-sm">
                              {candidate.interview_results.passed_count} / {candidate.interview_results.total_questions} Passed
                            </span>
                          </div>
                          {candidate.interview_results.iqa_remarks && (
                            <p className="text-sm text-slate-600">IQA: {candidate.interview_results.iqa_remarks}</p>
                          )}
                          {candidate.interview_results.expert_feedback && (
                            <p className="text-sm text-slate-600">Expert: {candidate.interview_results.expert_feedback}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Appeal Section */}
                    {candidate.case_status === 'appeal_pending' && candidate.appeal && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold text-slate-900 mb-4">Appeal Review</h3>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="font-medium mb-2">Candidate's Appeal Statement:</p>
                          <p className="text-slate-700 italic mb-4">"{candidate.appeal.candidate_remarks}"</p>
                          <div className="flex gap-3">
                            <Button 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleDecideAppeal('approved')}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve Appeal (Re-interview)
                            </Button>
                            <Button 
                              variant="destructive"
                              onClick={() => handleDecideAppeal('rejected')}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject Appeal
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* One-time hint to teach users to pick "Save as PDF" in the browser
          print dialog. Produces vector-quality PDFs (sharper than the
          rasterised Download PDF path). */}
      <Dialog open={printHintOpen} onOpenChange={setPrintHintOpen}>
        <DialogContent className="max-w-md" data-testid="print-hint-dialog">
          <DialogHeader>
            <DialogTitle>Save as PDF — best quality</DialogTitle>
            <DialogDescription>
              In the print dialog that opens next, change the <strong>Destination</strong> from your printer to <strong>"Save as PDF"</strong>.
              <br /><br />
              This produces a sharp, vector-quality PDF (text stays text and the QR code stays perfectly scannable, even when zoomed in).
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => confirmPrintHint(true)}
              data-testid="print-hint-dont-show-btn"
            >
              Got it — don't show again
            </Button>
            <Button
              onClick={() => confirmPrintHint(false)}
              data-testid="print-hint-continue-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Open print dialog
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resend Written Interview link — admin can set a new due date so
          candidates who missed the original window get a fresh chance. */}
      <Dialog
        open={resendDialog.open}
        onOpenChange={(o) => setResendDialog((s) => ({ ...s, open: o }))}
      >
        <DialogContent className="max-w-md" data-testid="resend-interview-dialog">
          <DialogHeader>
            <DialogTitle>
              Resend {resendDialog.target === "assessor" ? "Assessor" : "Candidate"} Interview Link
            </DialogTitle>
            <DialogDescription>
              A fresh email will be sent. The interview link itself stays the same — any earlier email you sent for this candidate will continue to work too.
              {resendDialog.target === "candidate"
                ? " Use this when the candidate can no longer find the email or needs an extended due date."
                : " Use this when the external assessor lost or can't access their scoring email."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label htmlFor="resend-deadline" className="text-sm">
                New due date <span className="text-slate-400">(optional — leave to keep current)</span>
              </Label>
              <Input
                id="resend-deadline"
                type="date"
                value={resendDialog.deadline || ""}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setResendDialog((s) => ({ ...s, deadline: e.target.value }))}
                data-testid="resend-interview-deadline-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Current due date: <span className="font-medium">{candidate?.written_interview?.deadline || "—"}</span>
                {candidate?.written_interview?.resend_count
                  ? ` · Previously resent ${candidate.written_interview.resend_count}×`
                  : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setResendDialog((s) => ({ ...s, open: false }))}
              data-testid="resend-interview-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResendInterviewLink}
              disabled={resendingInterview}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="resend-interview-confirm-btn"
            >
              {resendingInterview ? "Sending…" : "Send Fresh Link"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// Assessor Decision Dialog Component
function AssessorDecisionDialog({ awards, selectedLevel, selectedAwardTitle, onSubmit, aiRecommendation }) {
  const [selectedAward, setSelectedAward] = useState("");
  const [customAward, setCustomAward] = useState("");
  const [credits, setCredits] = useState("");
  const [comments, setComments] = useState("");
  const [open, setOpen] = useState(false);
  const [useCustom, setUseCustom] = useState(false);

  // Get AI-suggested awards
  const aiPrimaryAward = aiRecommendation?.primary_award || "";
  const aiAlternativeAwards = aiRecommendation?.alternative_awards || [];
  
  // Combine AI suggestions into a single list (removing duplicates)
  const aiSuggestedAwards = [aiPrimaryAward, ...aiAlternativeAwards].filter(Boolean);
  
  // Get all awards from the database for the same level (excluding AI suggestions to avoid duplicates)
  const databaseAwards = awards.filter(a => 
    !aiSuggestedAwards.includes(a.title)
  );
  
  // Check if we have any awards to show
  const hasAnyAwards = aiSuggestedAwards.length > 0 || databaseAwards.length > 0;

  // Auto-switch to custom if no awards available
  useEffect(() => {
    if (!hasAnyAwards) {
      setUseCustom(true);
    }
  }, [hasAnyAwards]);

  // Use the pre-selected award from scoring page when dialog opens
  useEffect(() => {
    if (open) {
      // Use the award selected from the scoring page dropdown
      if (selectedAwardTitle) {
        setSelectedAward(selectedAwardTitle);
      } else if (aiPrimaryAward && !selectedAward) {
        setSelectedAward(aiPrimaryAward);
      }
      
      // Set default credits based on level
      const defaultCredits = {
        diploma: "60",
        bachelor: "120",
        master: "180",
        doctoral: "240"
      };
      if (!credits) {
        setCredits(defaultCredits[selectedLevel] || "120");
      }
    }
  }, [open, selectedAwardTitle, aiPrimaryAward, selectedAward, selectedLevel, credits]);

  const handleSubmit = () => {
    const awardTitle = useCustom ? customAward : selectedAward;
    if (!awardTitle || !credits) {
      toast.error("Please enter an award title and credits");
      return;
    }
    onSubmit(awardTitle, credits, comments);
    setOpen(false);
    setSelectedAward("");
    setCustomAward("");
    setCredits("");
    setComments("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="assessor-decision-btn">Submit Decision</Button>
      </DialogTrigger>
      <DialogContent aria-describedby="assessor-dialog-desc" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assessor Final Decision</DialogTitle>
          <DialogDescription id="assessor-dialog-desc">
            Confirm the final award and credits for this candidate based on your assessment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Primary Award *</Label>
              {hasAnyAwards && (
                <button 
                  type="button"
                  onClick={() => setUseCustom(!useCustom)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {useCustom ? "Select from list" : "Enter custom award"}
                </button>
              )}
            </div>
            {useCustom || !hasAnyAwards ? (
              <Input
                value={customAward}
                onChange={(e) => setCustomAward(e.target.value)}
                placeholder="e.g., Bachelor of Science in Business Administration"
                data-testid="custom-award-input"
              />
            ) : (
              <Select value={selectedAward} onValueChange={setSelectedAward}>
                <SelectTrigger data-testid="final-award-select">
                  <SelectValue placeholder="Select primary award" />
                </SelectTrigger>
                <SelectContent>
                  {/* AI Suggested Awards Section - 1 Primary + 2 Alternatives */}
                  {aiSuggestedAwards.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50">
                        AI Suggested Awards (1 Primary + 2 Alternatives)
                      </div>
                      {aiSuggestedAwards.slice(0, 3).map((awardTitle, index) => (
                        <SelectItem 
                          key={`ai-${index}`} 
                          value={awardTitle}
                          className={index === 0 ? "font-medium" : ""}
                        >
                          {index === 0 ? `★ ${awardTitle} (Primary)` : `○ ${awardTitle} (Alternative ${index})`}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  
                  {/* Database Awards Section - Institution's Own */}
                  {databaseAwards.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-green-700 bg-green-50 mt-1">
                        Institution Awards ({selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)} Level)
                      </div>
                      {databaseAwards.map((award) => (
                        <SelectItem key={award.award_id} value={award.title}>
                          ◆ {award.title} ({award.total_credits} credits)
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            )}
            {!hasAnyAwards && (
              <p className="text-sm text-slate-500">No predefined awards for this level. Enter the award title manually.</p>
            )}
            {selectedAwardTitle && !useCustom && (
              <p className="text-xs text-green-600">
                ✓ Pre-selected from Scoring Page: {selectedAwardTitle}
              </p>
            )}
            {!selectedAwardTitle && aiPrimaryAward && !useCustom && (
              <p className="text-xs text-blue-600">
                ★ Credentia AI recommends: {aiPrimaryAward}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Total Credits *</Label>
            <Input
              type="number"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder="Enter total credits awarded (e.g., 120)"
              data-testid="final-credits-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Assessor Comments</Label>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Enter your assessment comments and justification..."
              data-testid="assessor-comments-input"
              rows={4}
            />
          </div>
          <Button 
            onClick={handleSubmit} 
            className="w-full" 
            disabled={(!selectedAward && !customAward) || !credits} 
            data-testid="submit-assessor-decision-btn"
          >
            Submit for IQA Review
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
